/**
 * LessonAdaptationCard — Blueprint tranche AD-3 (Feb 10, 2026).
 *
 * Supplementary evidence question rendered on the WorkMode
 * FEEDBACK stage AFTER Reflect Now / Q6 completes. Never
 * competes with Q6 for the primary reflection moment.
 *
 * Operator brief (locked):
 *   Question wording: "Did you have to change today's lesson plan?"
 *   Yes / No / Skip. Voice-first, typing secondary, no
 *   auto-keyboard, professional judgement always in charge.
 *
 * Yes path — one question at a time:
 *   1. Learning blocker (from closed registry)
 *   2. What did the lesson change to?
 *   3. Why was that the right teaching decision? (optional)
 *   4. Where did the lesson finish? (outcome)
 *   5. What should the next lesson focus on? (optional)
 *
 * No path — one tap saves a lightweight "no adaptation"
 * document so the fact is preserved as evidence.
 *
 * Skip — writes nothing and closes the section calmly.
 *
 * No consumer wiring in AD-3. This card only writes to the
 * `lesson_adaptations` collection; homework, briefings and
 * passports are wired in AD-5/6/7.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Check, X, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const OUTCOME_CHOICES = [
  { key: "returned_to_original", label: "Returned to today's plan" },
  { key: "partial_return",       label: "Partial return" },
  { key: "stayed_on_adaptation", label: "Stayed on the adaptation" },
];

const YES_STEPS = ["blocker", "adaptation", "reason", "outcome", "next", "review"];

export default function LessonAdaptationCard({ lesson, originalObjective, onResolved }) {
  const [phase, setPhase] = useState("gate"); // gate | done | yes | no
  const [step, setStep] = useState(YES_STEPS[0]);
  const [busy, setBusy] = useState(false);
  const [registry, setRegistry] = useState(null);

  // Yes-path answers
  const [primaryBlocker, setPrimaryBlocker] = useState(null);
  const [adaptation, setAdaptation] = useState("");
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [nextFocus, setNextFocus] = useState("");

  useEffect(() => {
    if (!lesson?.id) return;
    // Read the registry once on mount so the calm list is ready.
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/work-mode/lesson-adaptation/registry`,
          { withCredentials: true },
        );
        setRegistry(data);
      } catch {
        // Silent — the section only surfaces if we can present it
        // calmly. No error toast for the founder.
        setRegistry(null);
      }
    })();
    // Also read any existing evidence so the section reflects the
    // current state after a re-open.
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/work-mode/lessons/${lesson.id}/lesson-adaptation`,
          { withCredentials: true },
        );
        if (data?.adaptation) { setPhase("done"); onResolved?.("existing"); }
      } catch {
        // Endpoint 404 for lessons without instructor ownership.
        // Silent close is the correct behaviour.
      }
    })();
  }, [lesson?.id]);

  if (!lesson?.id) return null;
  if (phase === "done") {
    return (
      <div
        data-testid="ad3-adapt-card-saved"
        className="bg-emerald-900/15 border border-emerald-800/40 rounded-2xl px-4 py-3 flex items-center gap-3"
      >
        <Check className="w-4 h-4 text-emerald-300 flex-shrink-0" />
        <p className="text-sm text-emerald-200 leading-snug">
          Thanks — your adaptation notes are saved.
        </p>
      </div>
    );
  }

  const saveNoAdaptation = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/lesson-adaptation`,
        {
          adapted: false,
          original_objective: originalObjective || null,
          source: "instructor_typed",
        },
        { withCredentials: true },
      );
      setPhase("done");
      onResolved?.("no");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't save right now — try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const saveYesAdaptation = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/lesson-adaptation`,
        {
          adapted:                    true,
          original_objective:         originalObjective || null,
          primary_blocker:            primaryBlocker,
          adaptation:                 adaptation.trim(),
          educational_reason:         reason.trim() || null,
          outcome:                    outcome,
          next_lesson_recommendation: nextFocus.trim() || null,
          source:                     "instructor_typed",
        },
        { withCredentials: true },
      );
      setPhase("done");
      onResolved?.("yes");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't save right now — try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const advance = () => {
    const i = YES_STEPS.indexOf(step);
    if (i >= 0 && i < YES_STEPS.length - 1) setStep(YES_STEPS[i + 1]);
  };
  const back = () => {
    const i = YES_STEPS.indexOf(step);
    if (i > 0) setStep(YES_STEPS[i - 1]);
  };

  return (
    <section
      data-testid="ad3-adapt-card"
      className="bg-slate-800 rounded-2xl border border-slate-700 p-4 space-y-3"
    >
      <header className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-white leading-snug">
            Did you have to change today's lesson plan?
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            One extra question — helps us support you and the learner next time.
          </p>
        </div>
      </header>

      {phase === "gate" && (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            data-testid="ad3-adapt-yes-btn"
            onClick={() => { setPhase("yes"); setStep("blocker"); }}
            className="h-11 rounded-xl bg-blue-600/15 border-2 border-blue-500/40 text-blue-200 font-bold text-sm hover:border-blue-500/70"
          >
            Yes
          </button>
          <button
            type="button"
            data-testid="ad3-adapt-no-btn"
            onClick={saveNoAdaptation}
            disabled={busy}
            className="h-11 rounded-xl bg-slate-700 border-2 border-slate-600 text-slate-100 font-bold text-sm hover:border-slate-500 disabled:opacity-60"
          >
            No
          </button>
          <button
            type="button"
            data-testid="ad3-adapt-skip-btn"
            onClick={() => { setPhase("done"); onResolved?.("skip"); }}
            className="h-11 rounded-xl bg-transparent border-2 border-slate-700 text-slate-400 font-medium text-xs hover:text-slate-200"
          >
            Skip / Not now
          </button>
        </div>
      )}

      {phase === "yes" && (
        <div className="space-y-3 pt-1" data-testid="ad3-adapt-yes-flow">
          {step === "blocker" && registry?.blockers && (
            <div>
              <p className="text-xs text-slate-400 mb-2">What caused the lesson to change?</p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {registry.blockers.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    data-testid={`ad3-adapt-blocker-${b.key}`}
                    onClick={() => { setPrimaryBlocker(b.key); advance(); }}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      primaryBlocker === b.key
                        ? "bg-blue-600/20 border-blue-500/60 text-blue-100"
                        : "bg-slate-900/40 border-slate-700 text-slate-200 hover:border-slate-500"
                    }`}
                  >
                    <p className="text-sm font-bold leading-tight">{b.label}</p>
                    <p className="text-[11px] text-slate-400 leading-snug">{b.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "adaptation" && (
            <FreeTextStep
              testid="ad3-adapt-adaptation-field"
              label="What did the lesson change to?"
              value={adaptation}
              onChange={setAdaptation}
              onNext={() => adaptation.trim() && advance()}
              onBack={back}
              nextDisabled={!adaptation.trim()}
            />
          )}

          {step === "reason" && (
            <FreeTextStep
              testid="ad3-adapt-reason-field"
              label="Why was that the right teaching decision?"
              sublabel="Optional — one calm sentence is enough."
              value={reason}
              onChange={setReason}
              onNext={advance}
              onBack={back}
              nextDisabled={false}
              nextLabel={reason.trim() ? "Next" : "Skip"}
            />
          )}

          {step === "outcome" && (
            <div>
              <p className="text-xs text-slate-400 mb-2">Where did the lesson finish?</p>
              <div className="space-y-1.5">
                {OUTCOME_CHOICES.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    data-testid={`ad3-adapt-outcome-${o.key}`}
                    onClick={() => { setOutcome(o.key); advance(); }}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      outcome === o.key
                        ? "bg-blue-600/20 border-blue-500/60 text-blue-100"
                        : "bg-slate-900/40 border-slate-700 text-slate-200 hover:border-slate-500"
                    }`}
                  >
                    <p className="text-sm font-bold">{o.label}</p>
                  </button>
                ))}
              </div>
              <StepFooter onBack={back} rightHidden />
            </div>
          )}

          {step === "next" && (
            <FreeTextStep
              testid="ad3-adapt-next-field"
              label="What should the next lesson focus on?"
              sublabel="Optional — leave blank if you'll decide later."
              value={nextFocus}
              onChange={setNextFocus}
              onNext={advance}
              onBack={back}
              nextDisabled={false}
              nextLabel={nextFocus.trim() ? "Review" : "Skip to review"}
            />
          )}

          {step === "review" && (
            <div className="space-y-2">
              <ReviewLine label="Cause"      value={registryLabel(registry, primaryBlocker)} />
              <ReviewLine label="Adaptation" value={adaptation} />
              {reason.trim() && <ReviewLine label="Reason" value={reason} />}
              <ReviewLine label="Outcome"    value={OUTCOME_CHOICES.find((c) => c.key === outcome)?.label} />
              {nextFocus.trim() && <ReviewLine label="Next lesson" value={nextFocus} />}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button
                  type="button"
                  data-testid="ad3-adapt-review-back"
                  onClick={back}
                  className="h-10 rounded-lg border border-slate-700 text-slate-300 text-sm font-medium hover:border-slate-500"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid="ad3-adapt-save-btn"
                  onClick={saveYesAdaptation}
                  disabled={busy || !primaryBlocker || !adaptation.trim() || !outcome}
                  className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-60"
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── helpers ─────────────────────────────────────────────────────

function FreeTextStep({ testid, label, sublabel, value, onChange, onNext, onBack, nextDisabled, nextLabel }) {
  return (
    <div>
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {sublabel && <p className="text-[11px] text-slate-500 mb-2">{sublabel}</p>}
      {/*
        No auto-focus on mount — the mobile keyboard must never
        open by itself. The instructor taps the field deliberately.
      */}
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Tap to type — or use the voice recorder above."
        className="w-full bg-slate-900/60 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
      />
      <StepFooter onBack={onBack} onNext={onNext} nextDisabled={nextDisabled} nextLabel={nextLabel} />
    </div>
  );
}

function StepFooter({ onBack, onNext, nextDisabled, nextLabel = "Next", rightHidden }) {
  return (
    <div className="grid grid-cols-2 gap-2 pt-2">
      <button
        type="button"
        onClick={onBack}
        className="h-10 rounded-lg border border-slate-700 text-slate-300 text-sm font-medium hover:border-slate-500"
      >
        Back
      </button>
      {!rightHidden && (
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          className="h-10 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold disabled:opacity-50"
        >
          {nextLabel}
          <ChevronRight className="w-4 h-4 inline ml-1" />
        </button>
      )}
    </div>
  );
}

function ReviewLine({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm text-slate-200 leading-snug">{value}</p>
    </div>
  );
}

function registryLabel(registry, key) {
  if (!registry?.blockers || !key) return "—";
  const b = registry.blockers.find((x) => x.key === key);
  return b?.label || key;
}
